import { Link, useParams, useNavigate } from "react-router-dom";
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Divider, CircularProgress, Alert } from '@mui/material';
import Navbar from '../Layouts/Navbar';
import MenuIcon from '@mui/icons-material/Menu';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import api from '../../utils/api';



export default function DetailsVehicules() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  
  const [vehicle, setVehicle] = useState(null);
  const [vehicleModel, setVehicleModel] = useState(null);
  const [chauffeur, setChauffeur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  
  // Fetch vehicle data when component mounts
  useEffect(() => {
    const fetchVehicleData = async () => {
      if (!vehicleId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch vehicle details
        const vehicleResponse = await api.get(`/vehicles/${vehicleId}`);
        setVehicle(vehicleResponse.data);
        
        // Fetch model details if ID_Model exists
        if (vehicleResponse.data.ID_Model) {
          try {
            const modelResponse = await api.get(`/vehicle-models/${vehicleResponse.data.ID_Model}`);
            setVehicleModel(modelResponse.data);
          } catch (modelErr) {
            console.error('Error fetching vehicle model:', modelErr);
          }
        }
        
        // Fetch chauffeur details if ID_User exists
        if (vehicleResponse.data.ID_User) {
          try {
            const chauffeurResponse = await api.get(`/users/${vehicleResponse.data.ID_User}`);
            setChauffeur(chauffeurResponse.data);
          } catch (chauffeurErr) {
            console.error('Error fetching chauffeur details:', chauffeurErr);
          }
        }
        
        // Fetch vehicle image
        try {
          const imageResponse = await api.get(`/vehicles/${vehicleId}/image`, {
            responseType: 'blob'
          });
          const imageUrl = URL.createObjectURL(imageResponse.data);
          setImageUrl(imageUrl);
        } catch (imageErr) {
          console.error('Error fetching vehicle image:', imageErr);
          setImageError(true);
        }
      } catch (err) {
        console.error('Error fetching vehicle data:', err);
        setError('Failed to load vehicle data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVehicleData();
    
    // Cleanup function to revoke object URL
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [vehicleId]);
  
  // Handle edit button click
  const handleEdit = () => {
    // Navigate to update modal or page
    // This would depend on your application's routing structure
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };
  
  if (loading) {
    return (
      <>
        <Navbar />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress sx={{ color: 'red' }} />
        </Box>
      </>
    );
  }
  
  if (error) {
    return (
      <>
        <Navbar />
        <Box p={3}>
          <Alert severity="error">{error}</Alert>
          <Button 
            variant="outlined" 
            sx={{ mt: 2, color: 'red', borderColor: 'red' }}
            onClick={() => navigate('/vehicule')}
          >
            Retour à la liste des véhicules
          </Button>
        </Box>
      </>
    );
  }
  
  return (
    <>
      <Navbar />
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <LocalShippingIcon />
            <Typography variant="h6" ml={1}>VÉHICULE</Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              component={Link}
              to="/vehicule"
              variant="outlined"
              sx={{
                color: 'red',
                borderColor: 'red',
                textTransform: 'none',
                '&:hover': { backgroundColor: 'red', color: '#fff', borderColor: 'red' },
              }}
              startIcon={<MenuIcon />}
            >
              Liste Des Vehicules
            </Button>
            <Button
              component={Link}
              to={`/vehicule`}
              state={{ openUpdateModal: true, vehicleId: vehicleId }}
              variant="outlined"
              sx={{
                color: 'red',
                borderColor: 'red',
                textTransform: 'none',
                '&:hover': { backgroundColor: 'red', color: '#fff', borderColor: 'red' },
              }}
              startIcon={<EditIcon />}
            >
              Modifier
            </Button>
            <Button
              component={Link}
              to={`/vehicule`}
              state={{ openAddModal: true }}
              variant="outlined"
              sx={{
                color: 'red',
                borderColor: 'red',
                textTransform: 'none',
                '&:hover': { backgroundColor: 'red', color: '#fff', borderColor: 'red' },
              }}
              startIcon={<AddIcon />}
            >
              Ajouter
            </Button>
          </Box>
        </Box>

        {/* Information Section */}
        <Box sx={{ backgroundColor: '#000', p: 1, mb: 2 }}>
          <Typography variant="subtitle1" color="white">
            INFORMATIONS SUR LE VÉHICULE
          </Typography>
        </Box>
        
        {/* Vehicle Image */}
        {imageUrl && !imageError && (
          <Box mb={2} display="flex" justifyContent="center">
            <Box 
              component="img" 
              src={imageUrl} 
              alt="Vehicle" 
              sx={{ 
                maxWidth: '300px', 
                maxHeight: '200px', 
                objectFit: 'contain',
                border: '1px solid #ddd',
                borderRadius: '4px',
                p: 1
              }} 
              onError={() => setImageError(true)}
            />
          </Box>
        )}

        {/* Main Data Section - Updated with spans */}
        <Box flex={1} pr={2}>
          <Box display="flex" py={1}>
            <Box width="25%">
              <Typography sx={{fontWeight:'bold'}}>Marque</Typography>
            </Box>
            <Box width="25%">
              <Typography>{vehicleModel?.marque?.name || 'N/A'}</Typography>
            </Box>
            <Box width="25%">
              <Typography sx={{fontWeight:'bold'}}>	Modèle</Typography>
            </Box>
            <Box width="25%">
              <Typography>{vehicleModel?.name || 'N/A'}</Typography>
            </Box>
          </Box>
          <Divider />

          <Box display="flex" py={1}>
            <Box width="25%">
              <Typography sx={{fontWeight:'bold'}}>Type</Typography>
            </Box>
            <Box width="25%">
              <Typography>{vehicle?.type || 'N/A'}</Typography>
            </Box>
            <Box width="25%">
              <Typography sx={{fontWeight:'bold'}}>Type de moteur</Typography>
            </Box>
            <Box width="25%">
              <Typography>{vehicle?.type_moteur || 'N/A'}</Typography>
            </Box>
          </Box>
          <Divider />

          <Box display="flex" py={1}>
            <Box width="25%">
              <Typography sx={{fontWeight:'bold'}}>Etat physique du véhicule</Typography>
            </Box>
            <Box width="25%">
              <Typography>{vehicle?.etat || 'N/A'}</Typography>
            </Box>
            <Box width="25%">
              <Typography sx={{fontWeight:'bold'}}>	Date de mise en circulation</Typography>
            </Box>
            <Box width="25%">
              <Typography>{formatDate(vehicle?.date_circulation)}</Typography>
            </Box>
          </Box>
          <Divider />

          <Box display="flex" py={1}>
            <Box width="25%">
              <Typography sx={{fontWeight:'bold'}}>Kilométrage de départ</Typography>
            </Box>
            <Box width="25%">
              <Typography>{vehicle?.kilometrage || 'N/A'}</Typography>
            </Box>
            <Box width="25%">
              <Typography sx={{fontWeight:'bold'}}>Plaque d'immatriculation</Typography>
            </Box>
            <Box width="25%">
              <Typography>{vehicle?.immatriculation || 'N/A'}</Typography>
            </Box>
          </Box>
          <Divider />

          <Box display="flex" py={1}>
            <Box width="25%">
              <Typography sx={{fontWeight:'bold'}}>Date d’acquisition</Typography>
            </Box>
            <Box width="25%">
              <Typography>{formatDate(vehicle?.date_exp_inscription)}</Typography>
            </Box>
            <Box width="25%">
              <Typography sx={{fontWeight:'bold'}}>Nombre de places</Typography>
            </Box>
            <Box width="25%">
              <Typography>{vehicle?.seats || 'N/A'}</Typography>
            </Box>
          </Box>
          <Divider />

          <Box display="flex" py={1}>
            <Box width="25%">
              <Typography sx={{fontWeight:'bold'}}>En service ?</Typography>
            </Box>
            <Box width="75%">
              <Typography>{vehicle?.en_service ? 'Oui' : 'Non'}</Typography>
            </Box>
          </Box>
          <Divider />
        </Box>

        {/* Chauffeur Section */}
        <Box sx={{ backgroundColor: '#000', p: 1, mb: 2, mt: 2 }}>
          <Typography variant="subtitle1" color="white">
            AFFECTATIONS
          </Typography>
        </Box>
        
        {/* Chauffeur Information */}
        <Box flex={1} pr={2}>
          <Box display="flex" py={1}>
            <Box width="25%">
              <Typography sx={{fontWeight:'bold'}}>Chauffeur</Typography>
            </Box>
            <Box width="75%">
              <Typography>{chauffeur?.name || 'Non affecté'}</Typography>
            </Box>
          </Box>
          <Divider />
        </Box>
      </Box>
    </>
  );
}